/**
 * FeatureGate Component
 *
 * Wraps components/routes that require a specific feature flag to be enabled.
 * Shows a friendly "feature not available" message when disabled.
 * Includes error boundary protection to prevent crashes.
 *
 * @example
 * <ClassManagementGate>
 *   <ClassList />
 * </ClassManagementGate>
 *
 * @example
 * // With redirect instead of message
 * <ClassManagementGate redirect>
 *   <JoinClass />
 * </ClassManagementGate>
 */

import { Component } from 'react'
import PropTypes from 'prop-types'
import { Navigate } from 'react-router-dom'
import { useFeatureFlags } from '../hooks/useFeatureFlags'
import loggingService from '../services/loggingService'

/**
 * Standardized messages - must match server/constants/featureFlagMessages.js
 */
const FEATURE_FLAG_MESSAGES = {
  CLASS_MANAGEMENT_DISABLED: 'Class management features are not available'
}

/**
 * Error boundary for feature gate components.
 * Catches errors during render and displays a friendly fallback.
 * Prevents feature flag errors from crashing the entire application.
 */
class FeatureGateErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  componentDidCatch(error, errorInfo) {
    loggingService.error('Feature gate error boundary caught error', {
      type: 'feature_gate_error',
      error: error.toString(),
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      featureName: this.props.featureName,
      timestamp: new Date().toISOString()
    })
  }

  render() {
    if (this.state.hasError) {
      return <FeatureGateError featureName={this.props.featureName} />
    }
    return this.props.children
  }
}

FeatureGateErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  featureName: PropTypes.string
}

/**
 * Skeleton loader shown immediately while loading.
 * Provides visual feedback without jarring flash of content.
 */
const FeatureGateSkeleton = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div className="max-w-md w-full text-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="animate-pulse">
          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mb-4" />
          <div className="h-6 w-48 bg-gray-200 dark:bg-gray-700 rounded mx-auto mb-4" />
          <div className="h-4 w-64 bg-gray-200 dark:bg-gray-700 rounded mx-auto" />
        </div>
      </div>
    </div>
  </div>
)

/**
 * Error fallback component for feature gate errors.
 * Shown when a feature gate component throws an error.
 */
const FeatureGateError = ({ featureName }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div className="max-w-md w-full text-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-red-500 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Something Went Wrong
        </h1>
        <p className="text-gray-500 dark:text-gray-400 mb-4">
          We couldn&apos;t load {featureName || 'this feature'}. Please try again.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Reload Page
        </button>
      </div>
    </div>
  </div>
)

FeatureGateError.propTypes = {
  featureName: PropTypes.string
}

/**
 * Feature not available message.
 * Uses standardized message matching backend response.
 */
const FeatureNotAvailable = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
    <div className="max-w-md w-full text-center">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            className="w-8 h-8 text-gray-400 dark:text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Feature Not Available
        </h1>
        <p className="text-gray-500 dark:text-gray-400">{message}</p>
      </div>
    </div>
  </div>
)

FeatureNotAvailable.propTypes = {
  message: PropTypes.string.isRequired
}

/**
 * Inner component that uses the feature flag hook.
 * Separated to allow error boundary to catch hook errors.
 */
const ClassManagementGateInner = ({ children, redirect }) => {
  const { classManagementEnabled, isLoading } = useFeatureFlags()

  // Show skeleton immediately while loading (better UX than delayed spinner)
  if (isLoading) {
    return <FeatureGateSkeleton />
  }

  if (!classManagementEnabled) {
    if (redirect) {
      return <Navigate to="/dashboard" replace />
    }
    return <FeatureNotAvailable message={FEATURE_FLAG_MESSAGES.CLASS_MANAGEMENT_DISABLED} />
  }

  return children
}

ClassManagementGateInner.propTypes = {
  children: PropTypes.node.isRequired,
  redirect: PropTypes.bool
}

/**
 * Gate component for class management features.
 * Wraps children with error boundary protection.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render when feature is enabled
 * @param {boolean} [props.redirect=false] - Redirect to dashboard instead of showing message
 */
export const ClassManagementGate = ({ children, redirect = false }) => (
  <FeatureGateErrorBoundary featureName="class management">
    <ClassManagementGateInner redirect={redirect}>{children}</ClassManagementGateInner>
  </FeatureGateErrorBoundary>
)

ClassManagementGate.propTypes = {
  children: PropTypes.node.isRequired,
  redirect: PropTypes.bool
}

export default FeatureNotAvailable
